/**
 * A single shared `fetch` wrapper for every phase 21 (`AIA-81`) administration API module. Follows
 * `api/reporting.ts` and `api/organisationReporting.ts`'s own pattern (plain `fetch`,
 * `credentials: "same-origin"`, a discriminated-union result instead of throwing), extended with a
 * fourth outcome — `conflict` — because several of this phase's write endpoints return `409` with a
 * `detail` message that must reach the screen verbatim rather than as a generic failure (most
 * notably the role-revoke self-lockout guard, `RoleAdminEndpoints.RevokeRoleAsync`, and the
 * distribution-option/role name-collision guards).
 */
import { apiFetch } from "./apiFetch";

/** The outcome of one administration API call. */
export type AdminResult<TResponse> =
  | { kind: "ok"; response: TResponse }
  | { kind: "denied" }
  | { kind: "conflict"; message: string }
  | { kind: "error"; message: string };

interface ProblemDetails {
  detail?: string;
  title?: string;
}

/**
 * Issues one administration API call and interprets the result uniformly.
 *
 * @param path - The request path.
 * @param init - `fetch` options; `Content-Type: application/json` is added automatically when `body` is set.
 * @param label - A short, human-readable name for the endpoint, used only in generated error messages.
 * @returns The parsed body on success, or `denied`/`conflict`/`error` as appropriate.
 */
export async function adminRequest<TResponse>(path: string, init: RequestInit, label: string): Promise<AdminResult<TResponse>> {
  try {
    const headers: HeadersInit = { Accept: "application/json", ...(init.body ? { "Content-Type": "application/json" } : {}), ...init.headers };
    const response = await apiFetch(path, { credentials: "same-origin", cache: "no-store", ...init, headers });

    if (response.status === 403) {
      return { kind: "denied" };
    }

    if (response.status === 409) {
      return { kind: "conflict", message: await extractDetail(response, label) };
    }

    if (!response.ok) {
      return { kind: "error", message: await extractDetail(response, label) };
    }

    // 202 (e.g. a background sync started) and 204 carry no body.
    if (response.status === 202 || response.status === 204) {
      return { kind: "ok", response: undefined as TResponse };
    }

    const body = (await response.json()) as TResponse;
    return { kind: "ok", response: body };
  } catch {
    return { kind: "error", message: `The ${label} endpoint could not be reached.` };
  }
}

async function extractDetail(response: Response, label: string): Promise<string> {
  try {
    const body = (await response.json()) as ProblemDetails;
    return body.detail ?? body.title ?? `The ${label} endpoint returned ${String(response.status)}.`;
  } catch {
    return `The ${label} endpoint returned ${String(response.status)}.`;
  }
}
